import { useMemo } from "react";

import { Alert, CircularProgress, Stack } from "@mui/material";

import { useDutyScheduleCounselorOptions } from "@/pages/duty-schedule/hooks/useDutyScheduleCounselorOptions";
import { useDutyScheduleDetailContext } from "@/pages/duty-schedule/pages/duty-schedule-detail/DutyScheduleDetail.types";
import { useDutyScheduleDistributionSummary } from "@/pages/duty-schedule/pages/duty-schedule-detail/hooks/useDutyScheduleDistributionSummary";
import { DutyScheduleDistributionMetrics } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/distribution/components/metrics/DutyScheduleDistributionMetrics";
import { DutyScheduleDistributionPeriodComparison } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/distribution/components/period-comparison/DutyScheduleDistributionPeriodComparison";
import { DutyScheduleDistributionTable } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/distribution/components/table/DutyScheduleDistributionTable";
import { useTranslation } from "@/translations";

import { buildDistributionRows, sumDistributionRows } from "./DutyScheduleDistribution.utils";

// The filter state lives on the detail page rather than here, because this component unmounts whenever
// another tab is opened — same reasoning as DutyScheduleDataEntry and DutyScheduleCounselorOverview.
export function DutyScheduleDistribution() {
  const { t } = useTranslation();
  const { dutySchedule, counselorFilter } = useDutyScheduleDetailContext();
  const { counselors } = useDutyScheduleCounselorOptions();
  const { distributionSummary, isLoading, isError } = useDutyScheduleDistributionSummary(dutySchedule.id);

  // The duty counts themselves come from the backend, which also computes the Periodenvergleich totals
  // from the same rows — only the live counselor lookup (display name, sort order) happens here.
  const rows = useMemo(
    () => (distributionSummary == null ? [] : buildDistributionRows(distributionSummary.rows, counselors)),
    [distributionSummary, counselors]
  );

  const visibleRows = useMemo(
    () => (counselorFilter == null ? rows : rows.filter((row) => counselorFilter.includes(row.key))),
    [rows, counselorFilter]
  );

  const totalsRow = useMemo(() => sumDistributionRows(visibleRows), [visibleRows]);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress aria-label={t("dutySchedule.detail.loading")} />
      </Stack>
    );
  }

  if (isError || distributionSummary == null) {
    return <Alert severity="error">{t("dutySchedule.detail.distribution.loadError")}</Alert>;
  }

  return (
    <Stack flex={1} minHeight={0} gap={2}>
      <Alert severity="info">{t("dutySchedule.detail.distribution.hint")}</Alert>

      <Stack direction={{ xs: "column", lg: "row" }} gap={2} flex={1} minHeight={0}>
        <Stack flex={2} minHeight={0}>
          <DutyScheduleDistributionTable rows={visibleRows} totalsRow={totalsRow} />
        </Stack>

        <Stack flex={1} gap={2} minWidth={0}>
          <DutyScheduleDistributionPeriodComparison
            periods={distributionSummary.periodComparison}
            currentPlanName={dutySchedule.name}
          />
          <DutyScheduleDistributionMetrics metrics={distributionSummary.metrics} />
        </Stack>
      </Stack>
    </Stack>
  );
}
